import { motion } from "framer-motion";
import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import TechLogo from "@/components/sections/tech/TechLogos";
import TechDrawer from "@/components/sections/tech/TechDrawer";

/**
 * (04) THE ECOSYSTEM — "Technologies We Trust".
 * Premium, engineered aesthetic. Apple / Linear / Stripe level polish.
 * No neon, no excessive glow — subtle glass, subtle borders, elegant hover.
 * Mirrors the visual language of the public site's TechStack.jsx.
 */

const ITEMS = [
  {
    id: "react",
    name: "React",
    tagline: "Fast, scalable and ideal for products expected to grow.",
    powers: "The frontend interface of every page, every interaction, every product we ship.",
    whyWeUse:
      "Components stay reusable, the ecosystem keeps evolving, and developers we hire already know it.",
    alternatives: "Vue (smaller community), Angular (heavy), Svelte (less mature for hiring).",
    founder:
      "Think LEGO. You don't rebuild the entire house. You replace only the room you want to improve.",
    technical: "React 18, hooks-first, code-splitting, lazy boundaries, server-friendly architecture.",
  },
  {
    id: "fastapi",
    name: "FastAPI",
    tagline: "Modern Python API framework. Async, typed, documented automatically.",
    powers: "Every API endpoint, authentication, integrations, background tasks.",
    whyWeUse: "Type-safe, async-first, auto-generated docs, fast enough to handle real load.",
    alternatives: "Flask (no async), Django (too heavy), Express (less typed).",
    founder:
      "Like a sports car chassis. Lightweight, fast and built to handle serious workloads.",
    technical: "Python 3.12+, Pydantic v2, async I/O, OpenAPI specs out of the box.",
  },
  {
    id: "mongodb",
    name: "MongoDB",
    tagline: "A document database that grows with the product.",
    powers: "Application data, user accounts, content, analytics events.",
    whyWeUse:
      "Schema flexibility for early iteration; mature aggregation, indexing and transactions in production.",
    alternatives: "PostgreSQL (rigid schema, fits later stages), Firestore (vendor lock-in).",
    founder:
      "Adjustable shelves in a cabinet. Easy to reorganize as your product grows.",
    technical: "Replica sets, indexed queries, aggregation pipelines, time-series where it makes sense.",
  },
  {
    id: "gsap",
    name: "GSAP",
    tagline: "Animations that feel handcrafted, not generated.",
    powers: "Motion sequences on landing pages, scroll storytelling, hero transitions.",
    whyWeUse:
      "Precise control over timing and easing, performant on every device, no surprises across browsers.",
    alternatives:
      "Framer Motion (great for UI, less for cinematic), CSS animations (limited choreography).",
    founder:
      "Like a film editor working frame by frame, instead of letting auto-cut decide.",
    technical: "Timelines, ScrollTrigger, requestAnimationFrame-based, near-zero jank.",
  },
  {
    id: "openai",
    name: "OpenAI",
    tagline: "Reliable foundation-model layer for AI features.",
    powers: "AI chat, content generation, summarization, structured extraction.",
    whyWeUse:
      "Highest reliability and quality bar today, predictable APIs, strong rate handling.",
    alternatives: "Anthropic Claude (used selectively), open-source models (operational overhead).",
    founder:
      "We use AI where it saves time. Never where it creates noise.",
    technical: "GPT-class models, function calling, embeddings, streaming responses.",
  },
  {
    id: "python",
    name: "Python",
    tagline: "Readable, mature and ready for AI work.",
    powers: "Backend logic, data tasks, AI pipelines, scripts, integrations.",
    whyWeUse:
      "Massive ecosystem, AI-native libraries, easy to hire for, still readable months later.",
    alternatives:
      "Node.js (less suited for AI/data), Go (steeper for non-systems work), Rust (overkill).",
    founder:
      "Like English with brackets — easy to read, easy to maintain, easy to hand over.",
    technical: "Python 3.12, type hints, async, virtualenvs, modern packaging.",
  },
  {
    id: "cloudflare",
    name: "Cloudflare",
    tagline: "Edge network, security and performance in a single layer.",
    powers: "DNS, CDN, DDoS protection, edge caching, SSL.",
    whyWeUse:
      "Drops latency worldwide, blocks abuse before it hits the origin, free where most providers charge.",
    alternatives: "Fastly (premium pricing), Akamai (enterprise-only feel).",
    founder:
      "Like having a receptionist, security guard and traffic controller before visitors even enter your building.",
    technical: "Global anycast network, edge rules, R2 and Workers when needed.",
  },
  {
    id: "stripe",
    name: "Stripe",
    tagline: "Payments built like infrastructure, not a plugin.",
    powers: "One-time payments, subscriptions, invoicing, taxes, refunds.",
    whyWeUse:
      "Best-in-class developer experience, global coverage, predictable behaviour under load.",
    alternatives: "PayPal (older UX), GoPay (local but limited), Lemon Squeezy (newer, less mature).",
    founder: "Your cashier, subscription manager and accountant in one place.",
    technical: "Stripe Checkout & Elements, webhooks, idempotent APIs, EU VAT support.",
  },
  {
    id: "github",
    name: "GitHub",
    tagline: "Single source of truth for code, history and reviews.",
    powers: "Source control, code reviews, CI/CD, issue tracking.",
    whyWeUse:
      "Industry standard, integrations everywhere, traceable history of every change.",
    alternatives: "GitLab (self-host overhead), Bitbucket (smaller ecosystem).",
    founder:
      "Like a museum archive — every change recorded, every version recoverable.",
    technical: "Protected branches, PR reviews, GitHub Actions, environments per branch.",
  },
  {
    id: "figma",
    name: "Figma",
    tagline: "Design that engineers can actually read.",
    powers: "UI design, prototypes, design systems, client review.",
    whyWeUse:
      "Real-time collaboration in the browser, identical experience for design and engineering.",
    alternatives: "Sketch (Mac-only, single user), Adobe XD (deprecated direction).",
    founder:
      "Like an architect's blueprint — clear enough that the builders know exactly what to do.",
    technical: "Auto layout, components, variables, shared libraries.",
  },
  {
    id: "vercel",
    name: "Vercel",
    tagline: "Deploys that ship in seconds, not hours.",
    powers: "Frontend hosting, previews per pull request, edge functions.",
    whyWeUse: "Zero-config for React/Next, instant rollbacks, branch previews built in.",
    alternatives: "Netlify (similar), self-hosting (more ops), AWS (heavy lift).",
    founder: "Like a printing press for the web — push a file, see it live.",
    technical: "Edge runtime, ISR, image optimization, deploys per environment.",
  },
];

const DRAWER_LABELS = {
  eyebrow: "Stack",
  close: "Close",
  powers: "WHAT IT POWERS",
  whyWeUse: "WHY WE USE IT",
  alternatives: "ALTERNATIVES CONSIDERED",
  founder: "FOUNDER EXPLANATION",
  technical: "TECHNICAL DETAILS",
};

const EASE = [0.16, 1, 0.3, 1];

export default function Ecosystem() {
  const [activeIndex, setActiveIndex] = useState(null);
  const activeItem = activeIndex !== null ? ITEMS[activeIndex] : null;

  return (
    <section
      id="tech"
      data-nav-dark
      data-testid="ecosystem-section"
      className="relative overflow-hidden bg-[#050507] py-28 text-white md:py-36 lg:py-44"
    >
      {/* Faint engineered grid — same subtle treatment as TechStack */}
      <div className="grid-faint pointer-events-none absolute inset-0 opacity-[0.04]" />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
        {/* Header — eyebrow + asymmetric heading + tight subhead */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.85, ease: EASE }}
          className="mb-16 grid items-end gap-10 md:mb-24 md:grid-cols-12"
        >
          <div className="md:col-span-8">
            <div className="mb-6 flex items-center gap-3">
              <span className="h-px w-8 bg-white/30" />
              <span className="ff-ui text-[11px] uppercase tracking-[0.35em] text-white/55">
                (04) — Stack
              </span>
            </div>
            <h2 className="ff-display text-4xl font-bold leading-[1] tracking-tight text-white sm:text-5xl md:text-6xl lg:text-[5.4rem]">
              Technologies{" "}
              <span className="ff-serif font-normal text-white/55">we trust.</span>
            </h2>
          </div>
          <div className="md:col-span-4">
            <p className="ff-body max-w-md text-base leading-relaxed text-white/55">
              Chosen because they work.
              <br />
              Kept because they scale.
            </p>
          </div>
        </motion.div>

        {/* Cards grid */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-10%" }}
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.045 } },
          }}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          {ITEMS.map((item, i) => (
            <motion.button
              type="button"
              key={item.id}
              onClick={() => setActiveIndex(i)}
              data-cursor="big"
              data-testid={`tech-card-${item.id}`}
              variants={{
                hidden: { opacity: 0, y: 16 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.7, ease: EASE },
                },
              }}
              className="group relative flex h-full flex-col justify-between gap-8 rounded-2xl border border-white/[0.06] bg-white/[0.015] p-7 text-left backdrop-blur-[2px] transition-all duration-500 hover:border-white/[0.14] hover:bg-white/[0.03] focus:outline-none focus-visible:border-white/30"
            >
              {/* Top: logo + index */}
              <div className="flex items-start justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/[0.07] bg-white/[0.02] transition-colors duration-500 group-hover:border-white/[0.12]">
                  <TechLogo id={item.id} name={item.name} size={24} />
                </div>
                <span className="ff-ui text-[10px] uppercase tracking-[0.3em] text-white/25">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>

              {/* Middle: title + tagline */}
              <div className="flex flex-col gap-3">
                <h3 className="ff-display text-2xl tracking-tight text-white">
                  {item.name}
                </h3>
                <p className="ff-body text-[14.5px] leading-relaxed text-white/60">
                  {item.tagline}
                </p>
              </div>

              {/* Bottom: explore */}
              <div className="flex items-center justify-between border-t border-white/[0.05] pt-5">
                <span className="ff-ui text-[11px] uppercase tracking-[0.3em] text-white/55 transition-colors duration-500 group-hover:text-white">
                  Explore
                </span>
                <span className="grid h-8 w-8 place-items-center rounded-full border border-white/[0.08] text-white/55 transition-all duration-500 group-hover:border-white/25 group-hover:text-white">
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </span>
              </div>
            </motion.button>
          ))}
        </motion.div>

        {/* Editorial footnote */}
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          className="ff-serif mt-14 max-w-2xl text-lg text-white/50"
        >
          Tools selected to solve problems — not to follow trends.
        </motion.p>
      </div>

      <TechDrawer
        open={activeIndex !== null}
        onClose={() => setActiveIndex(null)}
        item={activeItem}
        labels={DRAWER_LABELS}
      />
    </section>
  );
}
